<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :header="dialogHeader"
    :closable="false"
    @hide="closeDialog"
    maximizable
    :style="{ width: '50rem' }"
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    @show="onDialogShow"
  >
    <div class="m-0 flex flex-col gap-4">
      <!-- From Field -->
      <div class="flex flex-col gap-1">
        <label for="fromEmail">From</label>
        <InputText
          id="fromEmail"
          v-model="fromValue"
          :disabled="!document?.from?.editable"
          placeholder="From email"
        />
      </div>

      <!-- To Recipients -->
      <div class="flex flex-col gap-1">
        <label for="toEmails">To</label>
        <InputChips
          id="toEmails"
          v-model="recipientsTo"
          @blur="handleToBlur"
          placeholder="Enter email addresses separated by comma"
          separator=","
          @add="validateTo"
          :class="{ 'p-invalid': toError }"
          :disabled="!document?.to?.editable"
        />
        <small
          v-if="toError"
          class="p-error"
        >
          {{ toError }}
        </small>
      </div>

      <!-- Additional CC Emails -->
      <div class="flex flex-col gap-1">
        <label for="ccEmails">CC</label>
        <InputChips
          id="ccEmails"
          v-model="recipientsCC"
          @blur="handleCCBlur"
          placeholder="Enter email addresses separated by comma"
          separator=","
          @add="validateCC"
          :class="{ 'p-invalid': ccError }"
          :disabled="!document?.cc?.editable"
        />
        <small
          v-if="ccError"
          class="p-error"
        >
          {{ ccError }}
        </small>
        <small 
          v-else-if="generalRecipientError" 
          class="p-error"
        >
          {{ generalRecipientError }}
        </small>

        <!-- Supervisor and Myself Toggles in compact format -->
        <div
          v-if="showSupervisorOption || showMyselfOption"
          class="flex flex-col gap-2 mb-2"
        >
          <div
            v-if="showSupervisorOption"
            class="flex items-center gap-2"
          >
            <ToggleSwitch
              v-model="boolSupervisor"
              :disabled="supervisorEmail === ''"
              class="scale-75 origin-left"
            />
            <span class="text-sm">Include Supervisor</span>
            <span class="text-sm text-gray-500 ml-1">
              ({{ supervisorEmail.toLowerCase() || "Not available" }})
            </span>
          </div>

          <div
            v-if="showMyselfOption"
            class="flex items-center gap-2"
          >
            <ToggleSwitch
              v-model="boolMyself"
              :disabled="!userEmail"
              class="scale-75 origin-left"
            />
            <span class="text-sm">Include Myself</span>
            <span class="text-sm text-gray-500 ml-1">
              ({{ userEmail.toLowerCase() || "Not available" }})
            </span>
          </div>
        </div>
      </div>
      <!-- Subject -->
      <div class="flex flex-col gap-1">
        <label for="emailSubject">Subject</label>
        <InputText
          id="emailSubject"
          v-model="subjectValue"
          :disabled="!document?.subject?.editable"
          placeholder="Email subject"
        />
      </div>

      <!-- Message Editor -->
      <div class="flex flex-col gap-1">
        <label>
          Message
          <span
            class="text-sm"
            v-if="attachmentNote"
          >
            [{{ attachmentNote }}]
          </span>
        </label>
        <Editor
          ref="editorRef"
          v-model="customMsg"
          class="w-full !z-10"
          editorStyle="height: 180px; width: 100%"
          :modules="editorModules"
          placeholder="Type your message here..."
          @load="onEditorLoad"
        />
      </div>
      <!-- Attachment Info -->
      <div class="text-sm text-gray-700">
        <div class="flex items-center">
          <span class="font-semibold">Attachment:</span>
          <span class="ml-2">
            {{ document?.fileName || "No file attached" }}
          </span>
          <button
            v-if="document?.pdfSrc"
            @click="emit('previewClicked')"
            class="ml-2 text-blue-500 underline text-sm hover:text-blue-700"
          >
            Preview
          </button>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2 mt-4">
        <Button
          label="Cancel"
          class="p-button-secondary"
          @click="closeDialog"
        />
        <Button
          :loading="isSending"
          label="Send"
          class="p-button-success"
          @click="sendEmail"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
  import Helper from "@/utils/helper";
  import { ref, watch } from "vue";
  const subjectValue = ref("");
  const fromValue = ref("");
  const editorRef = ref(null);
  const isSending = ref(false);
  const props = defineProps({
    visible: {
      type: Boolean,
      required: true
    },
    dialogHeader: {
      type: String,
      default: "Send Email"
    },
    document: {
      type: Object,
      default: () => ({})
    },
    // Default message content
    defaultMessage: {
      type: String,
      default:
        "<p>Hello,</p><p>Please find the attached document for your review.</p><p>Thank you.</p>"
    },
    // Show supervisor toggle option
    showSupervisorOption: {
      type: Boolean,
      default: true
    },
    showMyselfOption: {
      type: Boolean,
      default: true
    },
    // Specific attachment note to display
    attachmentNote: {
      type: String,
      default: "Document will be attached"
    }
  });

  const customMsg = ref("");
  const supervisorEmail = ref("");
  const userEmail = ref("");
  const recipientsTo = ref([]);
  const recipientsCC = ref([]);
  const toError = ref("");
  const ccError = ref("");
  const generalRecipientError = ref("");
  const boolSupervisor = ref(false);
  const boolMyself = ref(false);
  const editorModules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["clean"]
    ]
  };

  const emit = defineEmits([
    "update:visible",
    "emailSent",
    "emailError",
    "previewClicked"
  ]);

  // Watch supervisor toggle to add/remove email to CC list
  const syncEmailWithToggle = (toggle, email) => {
    watch(toggle, (enabled) => {
      if (!email.value) return;

      const index = recipientsCC.value.indexOf(email.value);

      if (enabled && index === -1) {
        recipientsCC.value.push(email.value);
      } else if (!enabled && index !== -1) {
        recipientsCC.value.splice(index, 1);
      }
    });
  };
  syncEmailWithToggle(boolSupervisor, supervisorEmail);
  syncEmailWithToggle(boolMyself, userEmail);

  // Watch CC list to sync with toggle buttons
  watch(
    recipientsCC,
    (newList) => {
      if (supervisorEmail.value && !newList.includes(supervisorEmail.value)) {
        boolSupervisor.value = false;
      }
      if (userEmail.value && !newList.includes(userEmail.value)) {
        boolMyself.value = false;
      }
    },
    { deep: true }
  );
  watch(
    [recipientsTo, recipientsCC],
    ([to, cc]) => {
      if (to.length > 0 || cc.length > 0) {
        generalRecipientError.value = "";
      }
    },
    { deep: true }
  );

  const closeDialog = () => {
    emit("update:visible", false);
  };

  // Initialize editor content when loaded
  const onEditorLoad = ({ instance }) => {
    if (instance) {
      instance.clipboard.dangerouslyPasteHTML(customMsg.value);
    }
  };

  // Dialog show event handler
  const onDialogShow = async () => {
    try {
      fromValue.value = props.document?.from?.value || "";
      subjectValue.value = props.document?.subject?.value || "";
      customMsg.value = props.defaultMessage;
      recipientsTo.value = props.document?.to?.value;
      recipientsCC.value = props.document?.cc?.value;

      toError.value = "";
      ccError.value = "";
      generalRecipientError.value = "";

      boolSupervisor.value = false;
      boolMyself.value = false;

      const oid = Helper.getCurrentUserOid();
      if (oid) {
        const managerEmail = await Helper.getManagerEmail(oid);
        supervisorEmail.value = managerEmail || "";
      }
      userEmail.value = Helper.getCurrentUserEmail();
    } catch (error) {
      Helper.showToast("Error", "Failed to initialize email dialog", "error");
    }
  };

  // Common email validation function for recipient fields
  const validateRecipientField = (recipients, errorRef) => {
    const lastEmail = recipients.value[recipients.value.length - 1];
    if (recipients.value.slice(0, -1).includes(lastEmail)) {
      errorRef.value = "Duplicate email address";
      recipients.value.pop();
      return;
    }
    if (!Helper.isValidEmail(lastEmail)) {
      errorRef.value = `Invalid email format: ${lastEmail}`;
      setTimeout(() => recipients.value.pop(), 0);
    } else {
      errorRef.value = "";
    }
  };

  // Validate To field on add
  const validateTo = () => validateRecipientField(recipientsTo, toError);

  // Validate CC field on add
  const validateCC = () => validateRecipientField(recipientsCC, ccError);

  // Handle To field blur
  const handleToBlur = (event) => {
    handleInputBlur(event, recipientsTo, toError);
  };

  // Handle CC field blur
  const handleCCBlur = (event) => {
    handleInputBlur(event, recipientsCC, ccError);
  };

  // Generic blur handler for email fields
  const handleInputBlur = (event, emailList, errorRef) => {
    const inputValue = event.target.value.trim();

    if (inputValue) {
      const newEmails = inputValue
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email);

      const invalidEmails = [];
      const duplicateEmails = [];

      newEmails.forEach((email) => {
        if (Helper.isValidEmail(email)) {
          if (!emailList.value.includes(email)) {
            emailList.value.push(email);
          } else {
            duplicateEmails.push(email);
          }
        } else {
          invalidEmails.push(email);
        }
      });

      event.target.value = "";

      const errorMessages = [];
      if (invalidEmails.length > 0) {
        invalidEmails.forEach((email) => {
          const index = emailList.value.indexOf(email);
          if (index > -1) {
            emailList.value.splice(index, 1);
          }
        });
        errorMessages.push(`Invalid email format: ${invalidEmails.join(", ")}`);
      }
      if (duplicateEmails.length > 0) {
        errorMessages.push(`Duplicate email(s): ${duplicateEmails.join(", ")}`);
      }
      errorRef.value = errorMessages.join(". ");
    } else {
      errorRef.value = "";
    }
    const invalidInList = emailList.value.filter(
      (email) => !Helper.isValidEmail(email)
    );
    if (invalidInList.length > 0) {
      errorRef.value = `Invalid email format: ${invalidInList.join(", ")}`;
    }
  };

  // Send email
  const sendEmail = async () => {
    try {
      isSending.value = true;
      if (recipientsTo.value.length === 0 && recipientsCC.value.length === 0) {
        generalRecipientError.value = "Please enter at least one recipient in either the \"To\" or \"CC\" field.";
        return;
      }

      const pdfBase64 = props.document.pdfSrc || "";

      let attachments = [];
      if (pdfBase64?.startsWith("data:application/pdf;base64,")) {
        attachments.push({
          name: props.document.fileName || "document.pdf",
          contentType: "application/pdf",
          contentBytes: pdfBase64.split(",")[1]
        });
      }
      const emailData = {
        body: customMsg.value,
        to: recipientsTo.value,
        subject: subjectValue.value,
        cc: recipientsCC.value,
        from: fromValue.value,
        attachments: attachments
      };

      if (import.meta.env.DEV) {
        console.log("Sending email with data:", emailData);
      }

      const response = await Helper.sendEmail(emailData);

      if (response.error) {
        throw new Error(response.error);
      }

      Helper.showToast("Success", "Email sent successfully", "success");

      emit("emailSent", {
        to: recipientsTo.value,
        cc: recipientsCC.value,
        subject: subjectValue.value,
        message: customMsg.value,
        from: fromValue.value
      });

      closeDialog();
    } catch (error) {
      console.error("Error sending email: ", error);
      Helper.showToast(
        "Error",
        error.message || "Failed to send email",
        "error"
      );

      emit("emailError", error);
    } finally {
      isSending.value = false;
    }
  };
</script>

<style scoped>
  input:disabled {
    background-color: #f1f5f9;
    color: #9ca3af;
    border: 1px solid #e5e7eb; 
    opacity: 1; 
  }
  .p-error {
    color: red;
    font-size: 0.875rem;
  }
</style>
